import React, { useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import { IMessage } from '../../redux/messages/types';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import PermMediaIcon from '@mui/icons-material/PermMedia';
import { UserProjectSearchDialogue } from '../../components/UserProjectSearchDialogue/UserProjectSearchDialogue';
import { useDispatch } from 'react-redux';
import { sendMessage } from '../../redux/messages/actions';
import { getJWtDetails } from '../../utils/AppUtils';
import { v4 as uuidv4 } from 'uuid';
import { IUserSummary } from '../../types/global/helper';

interface MediaRequestComponentProps {
    message: IMessage;
    isCurrentUser: boolean;
}

export const MediaRequestComponent: React.FC<MediaRequestComponentProps> = ({ message, isCurrentUser }) => {
    const { themeStyle } = useThemeContext();
    const dispatch = useDispatch();
    const [showProjectSearch, setShowProjectSearch] = useState(false);
    const { user_id, user } = getJWtDetails();

    // Determine media type from message (default to video)
    const requestedMediaType = message.message_content.toLowerCase().includes('picture')
        ? 'picture'
        : message.message_content.toLowerCase().includes('audio')
        ? 'audio'
        : 'video';

    const getMediaTypeLabel = (type: string) => {
        switch (type) {
            case 'picture':
                return 'Picture';
            case 'audio':
                return 'Audio';
            case 'video':
                return 'Video';
            default:
                return 'Media';
        }
    };

    const handleSelectMedia = () => {
        setShowProjectSearch(true);
    };

    const handleMediaSelected = (selectedMedia: any[]) => {
        if (selectedMedia && selectedMedia.length > 0) {
            const media = selectedMedia[0];
            const now = new Date().toISOString();
            const uniqueSocketID = uuidv4();

            // Determine message type based on selected media
            const messageType =
                requestedMediaType === 'picture'
                    ? 'picture_message'
                    : requestedMediaType === 'audio'
                    ? 'sound_message'
                    : 'video_message';

            // Send reply with media
            dispatch(
                sendMessage({
                    room: message.room._id,
                    created_at: now,
                    created_by: user_id,
                    message_content: `Here's the ${getMediaTypeLabel(requestedMediaType).toLowerCase()} you requested`,
                    message_type: messageType,
                    is_reply: true,
                    parent_message: message._id,
                    createdAt: now,
                    uniqueSocketID,
                    userObject: user as unknown as IUserSummary,
                    media: media._id,
                }),
            );
        }
    };

    return (
        <>
            <Box
                sx={{
                    width: { xs: '100%', sm: '280px' },
                    borderRadius: '16px',
                    overflow: 'hidden',
                    backgroundColor: theme[themeStyle].deep_grey,
                    border: `1px solid ${theme[themeStyle].grey}30`,
                    p: 2,
                }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PermMediaIcon sx={{ color: theme[themeStyle].highlight, fontSize: 40, mr: 2 }} />

                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: '16px',
                            color: theme[themeStyle].tag_white,
                        }}>
                        {getMediaTypeLabel(requestedMediaType)} Request
                    </Typography>
                </Box>

                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_REGULAR,
                        fontSize: '14px',
                        color: theme[themeStyle].grey,
                        mb: 2,
                    }}>
                    {message.message_content}
                </Typography>

                {!isCurrentUser && (
                    <Button
                        variant="contained"
                        onClick={handleSelectMedia}
                        fullWidth
                        sx={{
                            backgroundColor: theme[themeStyle].highlight,
                            color: theme[themeStyle].tag_white,
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: theme[themeStyle].highlight,
                            },
                        }}>
                        Select {getMediaTypeLabel(requestedMediaType)}
                    </Button>
                )}
            </Box>

            {/* Project Search Dialog */}
            <UserProjectSearchDialogue
                open={showProjectSearch}
                onClose={() => setShowProjectSearch(false)}
                type={requestedMediaType as 'audio' | 'video' | 'picture'}
                maxSelection={1}
                onSubmit={handleMediaSelected}
            />
        </>
    );
};
